import React, { useEffect, useMemo, useState } from 'react';
import {
  FiMenu,
  FiPlay,
  FiSearch,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import MediaCard from './components/MediaCard';
import MediaDetail from './components/MediaDetail';
import MediaSidebar from './components/MediaSidebar';
import MediaUploadModal from './components/MediaUploadModal';
import { mediaCategories } from './data/mediaSeed';
import useMediaLibrary from './hooks/useMediaLibrary';
import './AiMediaOn.scss';

const AiMediaOn = () => {
  const navigate = useNavigate();
  const { addMedia, media } = useMediaLibrary();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedMedia]);

  const filteredMedia = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase('ko-KR');
    return media.filter((item) => {
      const matchesCategory = selectedCategory === '전체' || item.category === selectedCategory;
      const searchable = [item.title, item.organization, item.description, ...(item.tags || [])]
        .join(' ')
        .toLocaleLowerCase('ko-KR');
      return matchesCategory && (!keyword || searchable.includes(keyword));
    });
  }, [media, query, selectedCategory]);

  const featuredMedia = useMemo(
    () => media.filter((item) => item.featured).slice(0, 3),
    [media],
  );

  const relatedItems = useMemo(() => (
    media.filter((item) => item.id !== selectedMedia?.id).slice(0, 6)
  ), [media, selectedMedia]);

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setSelectedMedia(null);
    setIsSidebarOpen(false);
  };

  const openTarget = (targetUrl) => {
    if (!targetUrl) return;
    if (targetUrl.startsWith('/')) {
      navigate(targetUrl);
      return;
    }
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleUpload = (newMedia) => {
    addMedia(newMedia);
    setIsUploadOpen(false);
    setSelectedCategory('전체');
    setQuery('');
    setSelectedMedia(newMedia);
  };

  return (
    <main className="ai-media-on">
      <header className="ai-media-header">
        <div className="ai-media-header__inner">
          <div className="ai-media-header__leading">
            <button type="button" className="ai-media-header__menu" onClick={() => setIsSidebarOpen((open) => !open)} aria-label="카테고리 메뉴">
              {isSidebarOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            </button>
          </div>
          <button type="button" className="ai-media-brand" onClick={() => { setSelectedMedia(null); selectCategory('전체'); }}>
            <span><FiPlay aria-hidden="true" /></span>
            <strong>AI 미디어 온</strong>
          </button>

          <form className="ai-media-search" onSubmit={(event) => event.preventDefault()} role="search">
            <input value={query} onChange={(event) => { setQuery(event.target.value); setSelectedMedia(null); }} placeholder="기관, 강사, 교재 또는 교육 소식 검색" aria-label="미디어 검색" />
            {query && <button type="button" className="ai-media-search__clear" onClick={() => setQuery('')} aria-label="검색어 지우기"><FiX aria-hidden="true" /></button>}
            <button type="submit" aria-label="검색"><FiSearch aria-hidden="true" /></button>
          </form>

          <div className="ai-media-header__actions">
            <button type="button" className="ai-media-header__upload" onClick={() => setIsUploadOpen(true)}>
              <FiUploadCloud aria-hidden="true" /><span>콘텐츠 등록</span>
            </button>
            <button type="button" className="ai-media-header__close" onClick={() => navigate('/menu')} aria-label="AI 미디어 온 닫기">
              <FiX aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="ai-media-shell">
        <div className={`ai-media-sidebar-wrap${isSidebarOpen ? ' is-open' : ''}`}>
          <MediaSidebar categories={mediaCategories} selectedCategory={selectedCategory} onSelect={selectCategory} />
        </div>
        {isSidebarOpen && <button type="button" className="ai-media-sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} aria-label="카테고리 메뉴 닫기" />}

        <section className="ai-media-content">
          {!selectedMedia && (
            <>
              <div className="ai-media-category-tabs" aria-label="카테고리 빠른 선택">
                {mediaCategories.map((category) => (
                  <button type="button" className={selectedCategory === category ? 'is-selected' : ''} onClick={() => selectCategory(category)} key={category}>{category}</button>
                ))}
              </div>

              {selectedCategory === '전체' && !query && featuredMedia.length > 0 && (
                <section className="ai-media-featured">
                  <header>
                    <div>
                      <h1>주목할 교육 소식</h1>
                      <p>인증된 교육기관이 전하는 새로운 프로그램과 설명회입니다.</p>
                    </div>
                    <span>기관 추천</span>
                  </header>
                  <div>{featuredMedia.map((item) => <MediaCard item={item} onSelect={setSelectedMedia} key={item.id} />)}</div>
                </section>
              )}

              <section className="ai-media-feed">
                <header>
                  <h2>{query ? `'${query}' 검색 결과` : selectedCategory === '전체' ? '새로 올라온 콘텐츠' : selectedCategory}</h2>
                  <span>{filteredMedia.length}개</span>
                </header>
                {filteredMedia.length > 0 ? (
                  <div className="ai-media-grid">
                    {filteredMedia.map((item) => <MediaCard item={item} onSelect={setSelectedMedia} key={item.id} />)}
                  </div>
                ) : (
                  <div className="ai-media-empty">
                    <FiSearch aria-hidden="true" />
                    <strong>검색 결과가 없습니다.</strong>
                    <p>다른 검색어나 카테고리를 선택해 보세요.</p>
                  </div>
                )}
              </section>
            </>
          )}

          {selectedMedia && (
            <MediaDetail
              item={selectedMedia}
              relatedItems={relatedItems}
              onBack={() => setSelectedMedia(null)}
              onOpenItem={setSelectedMedia}
              onOpenTarget={openTarget}
            />
          )}
        </section>
      </div>

      {isUploadOpen && (
        <MediaUploadModal categories={mediaCategories} onClose={() => setIsUploadOpen(false)} onSubmit={handleUpload} />
      )}
    </main>
  );
};

export default AiMediaOn;
